import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  alpha,
  useTheme,
  useMediaQuery,
  Tooltip,
  Select,
  MenuItem,
  Avatar,
} from "@mui/material";
import { MenuRounded, Brightness4, Brightness7 } from "@mui/icons-material";
import {
  ShieldCheck,
  ArrowLeft,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme as useThemeContext } from "../../../contexts/ThemeContext";
import { useDispatch, useSelector } from "react-redux";
import { updateAppPreferences } from "../../../redux/actions/userAction";
import FlagEmoji from "../../../components/common/FlagEmoji";

export const SIDEBAR_OPEN_WIDTH = 320;
export const SIDEBAR_CLOSED_WIDTH = 72;

const AdminPanelSidebar = ({
  tabs,
  activeTab,
  onTabChange,
  mobileOpen,
  onDrawerToggle,
  collapsed,
  onToggleCollapse,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { mode, toggleTheme } = useThemeContext();
  const appPreferences = useSelector((state) => state.user?.appPreferences);
  const [language, setLanguage] = useState(i18n.language || "en");
  const isDark = theme.palette.mode === "dark";

  useEffect(() => {
    if (appPreferences?.app_language) {
      setLanguage(appPreferences.app_language);
    }
  }, [appPreferences]);

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
    dispatch(
      updateAppPreferences({
        app_language: newLang,
        search_language: appPreferences?.search_language || newLang,
        countries: appPreferences?.countries || [],
      })
    );
  };

  const open = !collapsed;

  const sidebarContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Top: Logo + Collapse toggle */}
      {!isMobile && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: open ? "space-between" : "center",
            px: open ? 2 : 0,
            py: 2.5,
            minHeight: 64,
          }}
        >
          {open ? (
            <Box
              component={Link}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                cursor: "pointer",
                transition: "opacity 0.2s",
                "&:hover": { opacity: 0.8 },
              }}
            >
              <Box sx={{ height: 56, display: "flex", alignItems: "center" }}>
                <img
                  key={`admin-logo-${theme.palette.mode}`}
                  src={
                    isDark
                      ? "/logo/logo-full-dark-mode.svg"
                      : "/logo/logo-full.svg"
                  }
                  alt="Lawis"
                  style={{ height: "56px", width: "auto" }}
                />
              </Box>
            </Box>
          ) : (
            <Box
              onClick={onToggleCollapse}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                transition: "opacity 0.2s",
                "&:hover": { opacity: 0.8 },
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: "transparent",
                  flexShrink: 0,
                }}
              >
                <img
                  key={`admin-circle-${theme.palette.mode}`}
                  src={
                    isDark
                      ? "/logo/logo-circle-dark-mode.svg"
                      : "/logo/logo-circle.svg"
                  }
                  alt="Lawis Logo"
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              </Avatar>
            </Box>
          )}

          {open && (
            <Tooltip title={t("collapse")} placement="right">
              <IconButton
                onClick={onToggleCollapse}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  color: theme.palette.text.disabled,
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.text.primary, 0.06),
                    color: theme.palette.text.primary,
                  },
                }}
              >
                <ChevronsLeft size={16} strokeWidth={2} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Expand button (collapsed, desktop only) */}
      {!isMobile && collapsed && (
        <Box sx={{ display: "flex", justifyContent: "center", pb: 1 }}>
          <Tooltip title={t("expand")} placement="right">
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                color: theme.palette.text.disabled,
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.text.primary, 0.06),
                  color: theme.palette.text.primary,
                },
              }}
            >
              <ChevronsRight size={16} strokeWidth={2} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Separator */}
      <Box
        sx={{
          mx: open ? 2 : 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      />

      {/* Admin header section — only when expanded */}
      {open && (
        <Box
          sx={{
            p: 3,
            pb: 3,
            background: `linear-gradient(to bottom, ${alpha(
              theme.palette.primary.main,
              0.1
            )}, ${theme.palette.background.paper})`,
            borderRadius: 0,
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "16px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              mb: 2,
            }}
          >
            <ShieldCheck
              size={32}
              strokeWidth={1.5}
              color={theme.palette.primary.main}
            />
          </Box>

          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: "1.25rem",
              color: theme.palette.text.primary,
              mb: 0.5,
            }}
          >
            {t("admin_panel")}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
            }}
          >
            {t("admin_panel_description", "System management & monitoring")}
          </Typography>
        </Box>
      )}

      {/* Navigation menu */}
      <List sx={{ px: open ? 2 : 0.75, py: 1, flexGrow: 1 }}>
        {tabs.map((tab) => (
          <Tooltip
            key={tab.key}
            title={!open ? tab.label : ""}
            placement="right"
            arrow
          >
            <ListItem
              button
              disableRipple
              onClick={() => onTabChange(tab.key)}
              sx={{
                borderRadius: "12px",
                mb: 1,
                py: 1.5,
                justifyContent: open ? "flex-start" : "center",
                px: open ? 1.5 : 0,
                transition: "all 0.2s",
                ...(!open && {
                  width: 42,
                  minWidth: 42,
                  mx: "auto",
                }),
                bgcolor:
                  activeTab === tab.key
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                color:
                  activeTab === tab.key
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                "&:hover": {
                  bgcolor:
                    activeTab === tab.key
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.divider, 0.5),
                  color:
                    activeTab === tab.key
                      ? theme.palette.primary.main
                      : theme.palette.text.primary,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: "inherit",
                  minWidth: open ? 40 : "unset",
                  justifyContent: "center",
                  "& svg": { fontSize: "1.35rem" },
                }}
              >
                {tab.icon}
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary={tab.label}
                  primaryTypographyProps={{
                    fontSize: "1rem",
                    fontWeight: activeTab === tab.key ? 600 : 500,
                  }}
                />
              )}
              {open && activeTab === tab.key && (
                <Box
                  sx={{
                    width: 4,
                    height: 24,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "4px",
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        ))}
      </List>

      {/* Back to app — above the footer divider */}
      <Box
        sx={{
          px: open ? 2 : 0.75,
          mt: "auto",
          pb: 1.5,
        }}
      >
        <Tooltip
          title={!open ? t("back_to_app", "Back to app") : ""}
          placement="right"
          arrow
        >
          <ListItem
            button
            disableRipple
            onClick={() => navigate("/")}
            sx={{
              borderRadius: "12px",
              py: 1,
              px: open ? 1.5 : 0,
              justifyContent: open ? "flex-start" : "center",
              transition: "all 0.2s",
              color: theme.palette.text.secondary,
              ...(!open && {
                width: 42,
                minWidth: 42,
                mx: "auto",
              }),
              "&:hover": {
                bgcolor: alpha(theme.palette.divider, 0.5),
                color: theme.palette.text.primary,
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: "inherit",
                minWidth: open ? 32 : "unset",
                justifyContent: "center",
              }}
            >
              <ArrowLeft size={18} strokeWidth={1.75} />
            </ListItemIcon>
            {open && (
              <ListItemText
                primary={t("back_to_app", "Back to app")}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              />
            )}
          </ListItem>
        </Tooltip>
      </Box>

      {/* Footer — Theme, Language */}
      <Box
        sx={{
          px: open ? 2 : 0.75,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {/* Theme + Language row (expanded) */}
        {open && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Tooltip
              title={
                mode === "light" ? t("admin_dark_mode") : t("admin_light_mode")
              }
            >
              <IconButton
                onClick={toggleTheme}
                size="small"
                sx={{
                  borderRadius: "8px",
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {mode === "light" ? (
                  <Brightness4 sx={{ fontSize: 18 }} />
                ) : (
                  <Brightness7 sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>

            <Select
              value={language}
              onChange={handleLanguageChange}
              size="small"
              MenuProps={{
                anchorOrigin: { vertical: "top", horizontal: "center" },
                transformOrigin: { vertical: "bottom", horizontal: "center" },
              }}
              sx={{
                minWidth: 130,
                borderRadius: "8px",
                fontSize: "0.85rem",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  py: 0.75,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.divider,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <MenuItem
                value="fr"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "0.85rem",
                }}
              >
                <FlagEmoji emoji="🇫🇷" /> {t("language_french")}
              </MenuItem>
              <MenuItem
                value="en"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "0.85rem",
                }}
              >
                <FlagEmoji emoji="🇺🇸" /> {t("language_english")}
              </MenuItem>
            </Select>
          </Box>
        )}

        {/* Theme toggle only (collapsed) */}
        {!open && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Tooltip
              title={
                mode === "light" ? t("admin_dark_mode") : t("admin_light_mode")
              }
              placement="right"
            >
              <IconButton
                onClick={toggleTheme}
                size="small"
                sx={{
                  borderRadius: "8px",
                  color: theme.palette.text.secondary,
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.text.primary, 0.06),
                    color: theme.palette.text.primary,
                  },
                }}
              >
                {mode === "light" ? (
                  <Brightness4 sx={{ fontSize: 18 }} />
                ) : (
                  <Brightness7 sx={{ fontSize: 18 }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {open && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: theme.palette.text.secondary,
            }}
          >
            &copy; {new Date().getFullYear()} {t("company_name")}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar
          position="static"
          elevation={0}
          sx={{
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuRounded />
            </IconButton>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 600 }}
            >
              {t("admin_panel")}
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: SIDEBAR_OPEN_WIDTH,
              minWidth: SIDEBAR_OPEN_WIDTH,
              maxWidth: SIDEBAR_OPEN_WIDTH,
              bgcolor: theme.palette.background.paper,
              borderRight: `1px solid ${theme.palette.divider}`,
              boxSizing: "border-box",
              flexShrink: 0,
              borderRadius: 0,
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      ) : (
        /* Desktop Sidebar */
        <Paper
          elevation={0}
          sx={{
            width: collapsed ? SIDEBAR_CLOSED_WIDTH : SIDEBAR_OPEN_WIDTH,
            minWidth: collapsed ? SIDEBAR_CLOSED_WIDTH : SIDEBAR_OPEN_WIDTH,
            maxWidth: collapsed ? SIDEBAR_CLOSED_WIDTH : SIDEBAR_OPEN_WIDTH,
            flexShrink: 0,
            bgcolor: theme.palette.background.paper,
            borderRight: `1px solid ${theme.palette.divider}`,
            display: { xs: "none", md: "block" },
            position: "sticky",
            top: 0,
            height: "100vh",
            overflow: "auto",
            borderRadius: 0,
            transition: "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {sidebarContent}
        </Paper>
      )}
    </>
  );
};

export default AdminPanelSidebar;
